import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  Event as CalendarIcon,
  Person as PersonIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { StatusChip } from '../common';
import { 
  getReservationStatusLabel, 
  isReservationSuccessStatus,
  getStatusIconColor
} from '../../utils/statusUtils';

const ReservationSummary = ({ reservation, listing, onFinish }) => {
  const { t } = useTranslation(['common', 'booking']);
  
  const handleDownloadConfirmation = () => {
    // Créer un PDF ou document de confirmation
    const confirmationText = `
CONFIRMATION DE RÉSERVATION - KRILLOU

Numéro de réservation: ${reservation?.id}
Listing: ${listing?.title}
Dates: ${new Date(reservation?.start_date).toLocaleDateString('fr-FR')} - ${new Date(reservation?.end_date).toLocaleDateString('fr-FR')}
Montant total: €${reservation?.total_amount}
Statut: ${getReservationStatusLabel(reservation?.status)}

Codes d'accès:
- Code client: ${reservation?.client_code}
- Code propriétaire: ${reservation?.owner_code}

Pour toute question, contactez-nous.
    `;

    const element = document.createElement('a');
    const file = new Blob([confirmationText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `confirmation-${reservation?.id}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Box>
      {/* Titre avec icône de succès */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 80, 
            color: getStatusIconColor(reservation?.status), 
            mb: 2 
          }} 
        />
        <Typography variant="h4" gutterBottom sx={{ 
          color: isReservationSuccessStatus(reservation?.status) ? 'success.main' : 'text.primary'
        }}>
          {isReservationSuccessStatus(reservation?.status) ? 
            'Réservation confirmée !' : 
            `Réservation ${getReservationStatusLabel(reservation?.status)}`
          }
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {isReservationSuccessStatus(reservation?.status) ? 
            'Votre réservation a été créée avec succès.' :
            'Voici le résumé de votre réservation.'
          }
        </Typography>
      </Box>

      {/* Informations de réservation */}
      <Grid container spacing={3}>
        {/* Détails principaux */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Détails de votre réservation
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HomeIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">
                      {listing?.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {listing?.city}, {listing?.country}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">
                      Dates de réservation
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Du {new Date(reservation?.start_date).toLocaleDateString('fr-FR')} 
                      au {new Date(reservation?.end_date).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">
                      Nombre d'invités
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {reservation?.guest_count} personne{reservation?.guest_count > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EuroIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">
                      {t('booking:confirmation.payment.amount')}
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      €{reservation?.total_amount?.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Statut de la réservation
                </Typography>
                <StatusChip 
                  status={reservation?.status}
                  type="reservation"
                  size="medium"
                />
              </Box>

              {reservation?.special_requests && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Demandes spéciales
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {reservation.special_requests}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Codes d'accès et QR */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Codes d'accès
              </Typography>

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Code QR de votre réservation
                </Typography>
                <QRCodeSVG 
                  value={`${window.location.origin}/reservation/verify/${reservation?.id}`}
                  size={150}
                  level="H"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Code client
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main" fontWeight="bold">
                    {reservation?.client_code}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Code propriétaire
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main" fontWeight="bold">
                    {reservation?.owner_code}
                  </Typography>
                </Paper>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  Conservez ces codes précieusement. Ils seront nécessaires 
                  pour accéder à votre location.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Prochaines étapes */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Prochaines étapes
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary.main">
                  1
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Confirmation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vous recevrez un email de confirmation avec tous les détails.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary.main">
                  2
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Contact propriétaire
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Le propriétaire vous contactera pour organiser la remise des clés.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" color="primary.main">
                  3
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Profitez !
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Présentez-vous avec vos codes le jour J et profitez de votre séjour.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mt: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadConfirmation}
        >
          Télécharger la confirmation
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={onFinish}
          sx={{ minWidth: 200 }}
        >
          Voir mes réservations
        </Button>
      </Box>
    </Box>
  );
};

export default ReservationSummary;
